import React, { useState } from 'react';
import { Button } from '../../mawi-ui';
import { Input } from '../../mawi-ui';
import { Textarea } from '../../mawi-ui';
import { Card, CardContent, CardHeader, CardTitle } from '../../mawi-ui';
import { Badge } from '../../mawi-ui';
import { Play, Save, HelpCircle, Lightbulb, Code, FileText } from 'lucide-react';

interface BeginnerModeEditorProps {
  onSwitchToAdvanced: () => void;
  activeFile: any;
  onFileChange: (content: string) => void;
  onSave: () => void;
}

const BeginnerModeEditor: React.FC<BeginnerModeEditorProps> = ({
  onSwitchToAdvanced,
  activeFile,
  onFileChange,
  onSave,
}) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateCode = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    try {
      // Mock AI code generation - in real implementation, this would call an AI service
      const generatedCode = `// Generated code for: ${prompt}
//
// TODO: Implement the functionality described in the prompt above
// This is a placeholder - the actual AI generation would happen here

function exampleFunction() {
    console.log("Hello, World!");
    return "Generated code";
}

// End of generated code`;

      onFileChange(generatedCode);
      setPrompt('');
    } catch (error) {
      console.error('Failed to generate code:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex h-full bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
      <div className="flex-1 flex flex-col max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Code Creator
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Describe what you want to build, and AI will help you create the code!
            </p>
          </div>
          <div className="flex gap-2">
            <Badge variant="secondary" className="flex items-center gap-1">
              <HelpCircle className="h-3 w-3" />
              Beginner Mode
            </Badge>
            <Button variant="outline" onClick={onSwitchToAdvanced}>
              Switch to Advanced Editor
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1">
          {/* Left Panel - Input */}
          <Card className="flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                What do you want to build?
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col gap-4">
              <Textarea
                placeholder="Describe your app or feature in simple terms...

Examples:
- A todo list app with add, edit, and delete tasks
- A calculator that can do basic math operations
- A weather app that shows current temperature
- A simple blog with posts and comments"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="flex-1 min-h-[200px] resize-none"
              />

              <div className="flex gap-2">
                <Button
                  onClick={handleGenerateCode}
                  disabled={!prompt.trim() || isGenerating}
                  className="flex-1"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Generating Code...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Generate Code
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={onSave}>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </div>

              {/* Quick Prompts */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Quick Start Ideas:</h4>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    "A simple calculator app",
                    "A personal journal with date entries",
                    "A recipe book with ingredients and instructions",
                    "A habit tracker with daily checkmarks",
                  ].map((idea, index) => (
                    <button
                      key={index}
                      onClick={() => setPrompt(idea)}
                      className="text-left p-2 rounded border hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
                    >
                      {idea}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Panel - Code Preview */}
          <Card className="flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5 text-blue-500" />
                Generated Code
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1">
              {activeFile?.content ? (
                <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-auto max-h-[400px]">
                  <pre>{activeFile.content}</pre>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <FileText className="h-12 w-12 mb-4 opacity-50" />
                  <p className="text-center">
                    Your generated code will appear here.<br />
                    Describe what you want to build above!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Beginner Mode:</strong> Focus on describing what you want, not how to code it.
              AI will handle the technical details!
            </div>
            <Button variant="link" onClick={onSwitchToAdvanced}>
              Learn Advanced Features →
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeginnerModeEditor;